/**
 * @fileoverview Hooks pour la gestion du changement d'établissement
 *
 * Gère le switch entre companies avec support multi-device :
 * - Chaque device maintient sa propre company sélectionnée (localStorage)
 * - Le switch met à jour localStorage ET la BDD (pour le prochain login)
 * - Les autres devices ne sont pas affectés par un switch
 *
 * Les données companies sont lues depuis le session store (source de vérité unique),
 * pas via des appels SWR dédiés.
 *
 * @module useCompanySwitcher
 * @author Équipe Brydge
 * @since 3.0.0
 */

import {useCallback} from 'react';
import axiosInstance, {endpoints} from 'packages/common/utils/axios';
import useSWR from 'swr';
import {useSnackbar} from 'packages/common/components/snackbar';
import {useRouter} from 'packages/common/routes/hooks';
import {paths} from 'packages/common/routes/paths';
import {setSelectedCompany} from 'packages/common/utils/companyStorage';
import useSessionStore from 'packages/common/stores/sessionStore';

// ============================================
// Hook pour switcher de company
// ============================================
export const useCompanySwitch = () => {
  const {enqueueSnackbar} = useSnackbar();
  const router = useRouter();

  const switchCompany = useCallback(async (companyId, companyName, companySlug) => {
    // Sauvegarder l'état précédent pour pouvoir le restaurer en cas d'échec
    const previousCompanyId = useSessionStore.getState().activeCompany?._id;
    const previousCompanyName = useSessionStore.getState().activeCompany?.name;
    const previousCompanySlug = useSessionStore.getState().activeCompany?.slug;

    try {
      // Stocker dans localStorage de maniere optimiste
      setSelectedCompany(companyId, {
        name: companyName || null,
        slug: companySlug || null,
      });

      // Déléguer au session store : API call, SWR invalidation, session reload
      await useSessionStore.getState().switchCompany(companyId);

      enqueueSnackbar(
          `Établissement changé vers "${companyName}"`,
          {variant: 'success'},
      );

      router.push(paths.app.dashboard);
    } catch (error) {
      console.error('Erreur lors du switch d\'établissement:', error);

      // Restaurer le localStorage vers la company précédente en cas d'échec
      if (previousCompanyId) {
        setSelectedCompany(previousCompanyId, {
          name: previousCompanyName || null,
          slug: previousCompanySlug || null,
        });
      }

      let errorMessage = 'Erreur lors du changement d\'établissement';

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.status === 403) {
        errorMessage = 'Vous n\'avez pas accès à cet établissement';
      }

      // Aucun traitement du 401 ici : c'est le serveur qui déclare une session
      // finie, et l'intercepteur Axios s'en charge seul. Renvoyer vers la
      // connexion depuis ce hook court-circuiterait le nettoyage de session et
      // déconnecterait sur un 401 métier, ce que rien ne justifie.

      enqueueSnackbar(errorMessage, {variant: 'error'});
      throw error;
    }
  }, [enqueueSnackbar, router]);

  return switchCompany;
};

// ============================================
// Hook pour valider l'accès à une company
// ============================================
export const useValidateCompanyAccess = () => {
  const validateAccess = useCallback(async (companyId) => {
    try {
      const response = await axiosInstance.get(endpoints.auth.companies.validate(companyId));
      return response.data?.data?.hasAccess || false;
    } catch (error) {
      console.error('Erreur lors de la validation d\'accès:', error);
      return false;
    }
  }, []);

  return validateAccess;
};

// ============================================
// Hook pour l'historique des switches
// ============================================
export const useCompanySwitchHistory = (limit = 10) => {
  const {data, error, isLoading} = useSWR(
      `${endpoints.auth.companies.history}?limit=${limit}`,
      () => axiosInstance.get(`${endpoints.auth.companies.history}?limit=${limit}`).then((res) => res.data),
      {
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
      },
  );

  return {
    history: data?.data || [],
    count: data?.meta?.count || 0,
    isLoading,
    error,
  };
};

// ============================================
// Hook principal - Company Switcher complet
//
// Lit les donnees companies depuis le session store (source de verite unique).
// Aucun appel SWR supplementaire : les companies et la company active
// sont chargees une seule fois par le session endpoint au boot.
//
// @see useActiveCompany() pour consultation donnees company courante
// ============================================
export const useCompanySwitcher = () => {
  const companies = useSessionStore((s) => s.companies);
  const activeCompany = useSessionStore((s) => s.activeCompany);
  const switchCompany = useCompanySwitch();
  const validateAccess = useValidateCompanyAccess();

  const hasMultipleCompanies = companies.length > 1;

  const isCompanyActive = useCallback((companyId) => {
    return activeCompany?._id === companyId;
  }, [activeCompany]);

  return {
    companies,
    totalCompanies: companies.length,

    activeCompany,
    activeCompanyId: activeCompany?._id || null,

    // Actions
    switchCompany,
    validateAccess,

    // Helpers
    hasMultipleCompanies,
    isCompanyActive,
    hasActiveCompany: !!activeCompany,
  };
};
